/**
 * Querystring-opbouw voor de renovatieticket-endpoints.
 *
 * Zowel de vlakke lijst (`/reno-helpdesk/tickets`) als de per-project weergave
 * (`/reno-helpdesk/tickets/grouped`) filteren volledig backend-side en delen
 * exact dezelfde filterset. Die opbouw staat daarom hier, zodat een nieuw filter
 * automatisch in beide weergaven werkt.
 *
 * @module RenoHelpdesk/Utils/RenoTicketParams
 */

import { RenoTicketStatus } from '../types/renoTicket';

export interface RenoTicketFilters {
  search?: string;
  statuses?: RenoTicketStatus[];
  priorities?: string[];
  renovationProjectId?: number;
  projectPartId?: number;
  supplierId?: number;
  buildingId?: string;
  unitId?: string;
  messageTypes?: string[];
  assignedTo?: string;
  tags?: string[];
  /**
   * Enkel tickets met een ontvangen, nog niet gelezen mail — dezelfde tickets waar
   * het rode bolletje op de RS-/RT-knop staat. `undefined` = geen filter.
   */
  hasUnreadEmails?: boolean;
}

export interface RenoTicketQueryOptions {
  /** Fases uit de filterbalk; staan los van `filters` omdat ze eigen state hebben. */
  statuses?: RenoTicketStatus[];
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

/** Bouwt de querystring voor de ticket-endpoints op basis van de actieve filters. */
export function buildRenoTicketParams(
  filters: RenoTicketFilters,
  options: RenoTicketQueryOptions = {},
): URLSearchParams {
  const params = new URLSearchParams();

  if (options.page !== undefined) params.append('page', String(options.page));
  if (options.limit !== undefined) params.append('limit', String(options.limit));
  if (options.sortBy) params.append('sortBy', options.sortBy);
  if (options.sortOrder) params.append('sortOrder', options.sortOrder);

  if (filters.search) params.append('search', filters.search);

  const statuses = options.statuses ?? filters.statuses;
  statuses?.forEach((status) => params.append('statuses', status));
  filters.priorities?.forEach((priority) => params.append('priorities', priority));
  filters.messageTypes?.forEach((messageType) => params.append('messageTypes', messageType));
  filters.tags?.forEach((tag) => params.append('tags', tag));

  if (filters.renovationProjectId) {
    params.append('renovationProjectId', String(filters.renovationProjectId));
  }
  if (filters.projectPartId) {
    params.append('projectPartId', String(filters.projectPartId));
  }
  if (filters.supplierId) {
    params.append('supplierId', String(filters.supplierId));
  }
  if (filters.buildingId) params.append('buildingId', filters.buildingId);
  if (filters.unitId) params.append('unitId', filters.unitId);
  if (filters.assignedTo) params.append('assignedTo', filters.assignedTo);
  // Enkel meesturen wanneer het filter aan staat; `false` meesturen zou een filter
  // "tickets zonder ongelezen mail" suggereren, en dat bestaat niet.
  if (filters.hasUnreadEmails) params.append('hasUnreadEmails', 'true');

  return params;
}
